import { Box, Row, Txt } from "@stenajs-webui/core";
import { Label, PrimaryButton, SecondaryButton } from "@stenajs-webui/elements";
import { Checkbox, TextInput } from "@stenajs-webui/forms";
import { Window, WindowFooter } from "@stenajs-webui/modal";
import { useEffect, useState } from "react";
import { GapValues } from "@/lib/utils/GapValues";
import { RebateProgress } from "@/gql/graphql";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";

export const ExpectedVolumeForm = ({
  isOpen,
  rebate,
  onDone,
  onSave,
  loading,
}: {
  isOpen: boolean;
  rebate?: RebateProgress;
  onDone: () => void;
  onSave: (goalIds: string[], expectedVolume: number) => void;
  loading: boolean;
}) => {
  const [editedValue, setEditedValue] = useState<string>();
  const [useSuggested, setUseSuggested] = useState<boolean>(false);

  const useSuggestedChanged = (value: boolean) => {
    setEditedValue(
      (value
        ? rebate?.suggestedExpectedVolume
        : rebate?.expectedVolume
      )?.toString() ?? ""
    );
    setUseSuggested(value);
  };

  const save = () => {
    if (rebate && editedValue) {
      const expectedVolume = Number.parseInt(editedValue);
      if (rebate.goalIds.length > 0 && expectedVolume) {
        onSave(rebate.goalIds, expectedVolume);
      }
    }
  };

  useEffect(() => {
    if (rebate) {
      setEditedValue(rebate?.expectedVolume?.toString() ?? "");
    }
  }, [rebate]);

  const saveDisabled =
    loading ||
    editedValue?.length === 0 ||
    rebate?.expectedVolume?.toString() === editedValue;

  return (
    <Window
      isOpen={isOpen}
      onRequestClose={onDone}
      headerText="Change Expected Volume"
      indent={GapValues.TWENTYFOUR}
    >
      <Box
        gap={GapValues.TWENTYEIGHT}
        data-testid={testIdConstants.expectedVolumeModal}
      >
        <Row gap={GapValues.TWENTYFOUR}>
          <Label text="Agreement">
            <Txt>
              {`${rebate?.rebateAgreement?.rebateAgreementNumber} ${rebate?.rebateAgreement?.name}`}
            </Txt>
          </Label>
          <Label text="Current Expected volume">
            <Txt data-testid={testIdConstants.expectedVolumeModalCurrentValue}>
              {rebate?.expectedVolume}
            </Txt>
          </Label>
          <Label text="Suggested Expected volume">
            <Txt
              data-testid={testIdConstants.expectedVolumeModalSuggestedValue}
            >
              {rebate?.suggestedExpectedVolume}
            </Txt>
          </Label>
        </Row>

        <Row gap={GapValues.TWENTYFOUR}>
          <Label text="New Expected volume">
            <TextInput
              value={editedValue}
              onValueChange={setEditedValue}
              disabled={useSuggested}
              data-testid={
                testIdConstants.expectedVolumeModalNewExpectedVolumeInput
              }
            />
          </Label>
          <Label text="Use Suggested">
            <Checkbox
              value={useSuggested}
              onValueChange={useSuggestedChanged}
              data-testid={
                testIdConstants.expectedVolumeModalUseSuggestedCheckbox
              }
            />
          </Label>
        </Row>
      </Box>
      <WindowFooter>
        <Row gap={GapValues.SIXTEEN} justifyContent="flex-end">
          <SecondaryButton
            label="Close"
            onClick={() => onDone()}
            data-testid={testIdConstants.expectedVolumeModalCloseButton}
          />
          <PrimaryButton
            label="Save"
            data-testid={testIdConstants.expectedVolumeModalSaveButton}
            disabled={saveDisabled}
            onClick={() => save()}
            loading={loading}
          />
        </Row>
      </WindowFooter>
    </Window>
  );
};
